import React, { useCallback } from 'react';

import { IconProps, InputProps } from '@rbilabs/universal-components';
import { CustomEventNames, EventTypes, logEvent } from 'features/analytics';
import { Keyboard } from 'react-native';

import AutocompleteOptionsDropdown from 'components/ucl/autocomplete-options-dropdown';
import { AutocompleteDropdownProps } from 'components/ucl/autocomplete-options-dropdown/types';
import useLocationPredictions from 'hooks/geolocation/use-location-predictions';
import useLocationPredictionsDelivery from 'hooks/geolocation/use-location-predictions-delivery';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { IPlaceData } from 'utils/geolocation';
import noop from 'utils/noop';

type BaseInputProps = Pick<InputProps, 'value'>;
type WebInputCustomProps = InputProps & {
  name?: string;
};

export interface IAutocompleteAddressInputProps extends BaseInputProps {
  testID: string;
  autoFocus?: boolean;
  label: string;
  labelColor?: string;
  addressesOnly?: boolean;
  useDeliveryPredictions?: boolean;
  required: boolean;

  message?: string;
  setMessage?: (message: string) => void;

  onAddressSelected: (address: string, placeId?: string, placeData?: IPlaceData) => void;
  onInputValueChange: (value: string) => void;
  onBlur?(): void;
  inputCustomProps?: WebInputCustomProps;
  rightIconCustomProps?: IconProps;
  inputWrapper?: AutocompleteDropdownProps['inputWrapper'];
  maskSuggestionsForSessionReplay?: AutocompleteDropdownProps['maskSuggestionsForSessionReplay'];
  coverSuggestionTouches?: AutocompleteDropdownProps['coverSuggestionTouches'];
  /**
   * Web store search must not use browser address autofill. A saved address
   * fills the text without a Places id, so nearby restaurants never load.
   */
  disableBrowserAutofill?: boolean;
}

export const AutocompleteAddressInput = React.forwardRef<
  HTMLInputElement,
  IAutocompleteAddressInputProps
>(
  (
    {
      testID,
      label,
      labelColor,
      message,
      addressesOnly = false,
      useDeliveryPredictions = false,
      onAddressSelected = noop,
      onInputValueChange,
      onBlur,
      setMessage,
      value = '',
      inputCustomProps,
      rightIconCustomProps,
      inputWrapper,
      maskSuggestionsForSessionReplay,
      coverSuggestionTouches,
      disableBrowserAutofill = false,
    },
    ref
  ) => {
    /**
     * Retrieve predictions from the desired source and only pass the first five results.
     */
    const enableDoordashSuggestions =
      useFlag(LaunchDarklyFlag.ENABLE_DOORDASH_DRIVE_SUGGESTIONS) && useDeliveryPredictions;

    const deliveryPredictions = useLocationPredictionsDelivery(value, {
      enabled: enableDoordashSuggestions,
    });

    const defaultPredictions = useLocationPredictions(value, {
      addressesOnly,
      enabled: !enableDoordashSuggestions,
    });

    const predictions = (enableDoordashSuggestions
      ? deliveryPredictions
      : defaultPredictions
    )?.slice(0, 5);

    const handleDropdownValueSelected = useCallback(
      ({ address, placeId, placeData }: any) => {
        onAddressSelected(address, placeId, placeData);
        logEvent(CustomEventNames.MANUAL_LOCATION_SEARCH, EventTypes.Search);
      },
      [onAddressSelected]
    );

    const handleInputValueChange = useCallback(
      (inputValue: string | undefined) => {
        onInputValueChange(inputValue || '');

        if (setMessage) {
          setMessage('');
        }
      },
      [onInputValueChange, setMessage]
    );

    return (
      <AutocompleteOptionsDropdown
        noPredictionsFoundText={t('noLocationsMatchYourSearch')}
        testID={testID}
        onSelection={handleDropdownValueSelected}
        predictions={predictions}
        onInputChange={handleInputValueChange}
        onBlur={onBlur}
        value={value}
        label={label}
        labelColor={labelColor}
        errorMessage={message}
        ref={ref}
        clearTextIconTitle={t('clearText')}
        inputCustomProps={{
          textContentType: disableBrowserAutofill ? 'none' : 'fullStreetAddress',
          importantForAutofill: disableBrowserAutofill ? 'no' : 'yes',
          // Web-only store locator flow disables browser autofill so users must
          // choose a places suggestion (which includes a placeId).
          autoComplete: disableBrowserAutofill ? 'off' : 'street-address',
          // Keep the delivery/web autofill hint on the default path.
          name: disableBrowserAutofill ? undefined : 'street-address',
          returnKeyType: 'done',
          onSubmitEditing: () => Keyboard.dismiss(),
          ...inputCustomProps,
        }}
        rightIconCustomProps={rightIconCustomProps}
        inputWrapper={inputWrapper}
        maskSuggestionsForSessionReplay={maskSuggestionsForSessionReplay}
        coverSuggestionTouches={coverSuggestionTouches}
      />
    );
  }
);

export default AutocompleteAddressInput;
